import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getOrganizations } from "@/lib/api";
import { formatDate, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export function Organizations() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const perPage = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations", page, search, showInactive],
    queryFn: () =>
      getOrganizations({
        page,
        per_page: perPage,
        search,
        include_inactive: showInactive,
      }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        Error: {error.message}
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit">Search</Button>
            {search && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSearch("");
                  setSearchInput("");
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </form>
          <label className="flex items-center gap-2 mt-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setPage(1);
              }}
              className="rounded border-gray-300"
            />
            Show deactivated organizations
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : data?.organizations.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">
                {search ? "No organizations found" : "No organizations yet"}
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Accounts</TableHead>
                    <TableHead>Import</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead className="text-right">Lifetime spend</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.organizations.map((org) => (
                    <TableRow
                      key={org.id}
                      className={!org.is_active ? "opacity-50" : ""}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{org.email}</p>
                          {!org.is_active && (
                            <Badge variant="secondary" className="text-xs">
                              Deactivated
                            </Badge>
                          )}
                        </div>
                        {org.company_name && (
                          <p className="text-xs text-muted-foreground">
                            {org.company_name}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {org.accounts.length === 0 ? (
                          <span className="text-muted-foreground">-</span>
                        ) : (
                          <div>
                            <p className="font-medium">
                              {org.accounts[0]?.account_name ||
                                org.accounts[0]?.external_account_id.slice(
                                  0,
                                  8,
                                )}
                            </p>
                            <div className="flex gap-1 items-center">
                              <Badge variant="outline">
                                {org.accounts[0]?.service_code}
                              </Badge>
                              {org.accounts.length > 1 && (
                                <span className="text-xs text-muted-foreground">
                                  +{org.accounts.length - 1}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {org.import_status === "ok" && (
                          <Badge variant="success">OK</Badge>
                        )}
                        {org.import_status === "failed" && (
                          <Badge variant="destructive">Failed</Badge>
                        )}
                        {org.import_status === null && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {org.credits_balance}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {org.lifetime_spend.length === 0
                          ? "-"
                          : org.lifetime_spend
                              .map((s) => formatCurrency(s.amount, s.currency))
                              .join(" + ")}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {org.last_activity_at
                          ? formatDate(org.last_activity_at)
                          : "-"}
                        {org.last_activity_type &&
                          org.last_activity_type !== "signup" && (
                            <Badge variant="outline" className="ml-2">
                              {
                                {
                                  payment: "payment",
                                  ai_chat: "AI",
                                  import: "import",
                                }[org.last_activity_type]
                              }
                            </Badge>
                          )}
                      </TableCell>
                      <TableCell>
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/organizations/${org.id}`}>View</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-3">
                  <div className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({data?.total} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Backwards compatibility export
export { Organizations as Customers };
